/**
 * @name isValidAFTransition
 * @description Valida uma transição para um Autômato Finito (AF).
 * A transição deve ser composta por um ou mais símbolos (letras, números, λ) separados por vírgula.
 * Aceita espaços em branco e uma vírgula opcional no final.
 * Ex: 'a', 'b', '1', 'a,b', 'a, b', 'a,b,', 'λ', 'a,λ'
 * @param {string} label O rótulo da transição.
 * @returns {boolean} Retorna true se o rótulo for válido para um AF, caso contrário, false.
 */
function isValidAFTransition(label) {
  const regex = /^\s*[a-zA-Z0-9λ]+\s*(,\s*[a-zA-Z0-9λ]+\s*)*,?\s*$/;
  return regex.test(label);
}

/**
 * @name isValidAPTransition
 * @description Valida uma transição para um Autômato com Pilha (AP).
 * O formato deve ser uma lista de 'símbolo_lido,desempilhar/empilhar', separadas por vírgula.
 * Ex: 'a X/Y', 'a X/λ, b Y/Z'
 * @param {string} label O rótulo da transição.
 * @returns {boolean} Retorna true se o rótulo for válido para um AP, caso contrário, false.
 */
function isValidAPTransition(label) {
  const singleTransitionRegex =
    /^\s*[a-zA-Z0-9λ]\s+[a-zA-Z0-9λ]+\/[a-zA-Z0-9λ]+\s*$/;
  const multipleTransitionsRegex =
    /^\s*([a-zA-Z0-9λ]\s+[a-zA-Z0-9λ]+\/[a-zA-Z0-9λ]+)(\s*,\s*[a-zA-Z0-9λ]\s+[a-zA-Z0-9λ]+\/[a-zA-Z0-9λ]+)*,?\s*$/;

  // prettier-ignore
  return singleTransitionRegex.test(label) || multipleTransitionsRegex.test(label);
}

/**
 * @name isValidALLTransition
 * @description Valida uma transição para um Autômato Linearmente Limitado (ALL).
 * O formato deve ser 'símbolo_lido/símbolo_escrito direção', podendo ser uma lista separada por vírgula.
 * Ex: 'a/a R', 'b/c L, d/e R'
 * @param {string} label O rótulo da transição.
 * @returns {boolean} Retorna true se o rótulo for válido para um ALL, caso contrário, false.
 */
function isValidALLTransition(label) {
  const singleTransitionRegex =
    /^\s*[a-zA-Z0-9λ<>#]\s*\/\s*[a-zA-Z0-9λ<>#]\s*([LREDS])\s*$/;
  const multipleTransitionsRegex =
    /^\s*([a-zA-Z0-9λ<>#]\s*\/\s*[a-zA-Z0-9λ<>#]\s*[LREDS])(\s*,\s*[a-zA-Z0-9λ<>#]\s*\/\s*[a-zA-Z0-9λ<>#]\s*[LREDS])*,?\s*$/;

  // prettier-ignore
  return singleTransitionRegex.test(label) || multipleTransitionsRegex.test(label);
}

/**
 * @name isValidMTTransition
 * @description Valida uma transição para uma Máquina de Turing (MT).
 * O formato deve ser 'símbolo_lido/símbolo_escrito direção', podendo ser uma lista separada por vírgula.
 * Ex: 'a/a R', 'b/c L, d/e R'
 * @param {string} label O rótulo da transição.
 * @returns {boolean} Retorna true se o rótulo for válido para uma MT, caso contrário, false.
 */
function isValidMTTransition(label) {
  const singleTransitionRegex =
    /^\s*[a-zA-Z0-9λ#]\s*\/\s*[a-zA-Z0-9λ#]\s*([LREDS])\s*$/;
  const multipleTransitionsRegex =
    /^\s*([a-zA-Z0-9λ#]\s*\/\s*[a-zA-Z0-9λ#]\s*[LREDS])(\s*,\s*[a-zA-Z0-9λ#]\s*\/\s*[a-zA-Z0-9λ#]\s*[LREDS])*,?\s*$/;

  // prettier-ignore
  return singleTransitionRegex.test(label) || multipleTransitionsRegex.test(label);
}

/**
 * @name validateTransition
 * @description Função principal para validar o rótulo de uma transição com base no tipo de autômato.
 * @param {string} label O rótulo da transição.
 * @param {string} automatonType O tipo de autômato (ex: 'AF', 'AP', 'ALL', 'MT').
 * @returns {boolean} Retorna true se a validação for bem-sucedida, caso contrário, false.
 */
export function validateTransition(label, automatonType) {
  const trimmedLabel = label.trim();

  const validators = {
    AF: isValidAFTransition,
    AP: isValidAPTransition,
    ALL: isValidALLTransition,
    MT: isValidMTTransition,
  };

  const validator = validators[automatonType];

  if (validator) {
    return validator(trimmedLabel);
  }

  return true;
}

/**
 * @name isValidAFPartialTransition
 * @description Valida uma transição parcial para um AF. Permite a entrada incompleta.
 * Ex: '', 'a', 'a,', 'a,b', 'a, b,'
 * @param {string} label O rótulo da transição.
 * @returns {boolean} Retorna true se a string parcial for válida, caso contrário, false.
 */
function isValidAFPartialTransition(label) {
  // A string vazia é um ponto de partida válido.
  if (label.length === 0) {
    return true;
  }

  const trimmedLabel = label.trim();
  const endsWithComma = trimmedLabel.endsWith(",");
  const labelToValidate = endsWithComma
    ? trimmedLabel.slice(0, -1)
    : trimmedLabel;

  const regex = /^\s*[a-zA-Z0-9λ]+(\s*,\s*[a-zA-Z0-9λ]+)*\s*$/;
  return regex.test(labelToValidate);
}

/**
 * @name isValidAPPartialTransition
 * @description Valida uma transição parcial para um AP. Permite a entrada incompleta.
 * Ex: '', 'a', 'a X', 'a X/', 'a X/Y', 'a X/Y,'
 * @param {string} label O rótulo da transição.
 * @returns {boolean} Retorna true se a string parcial for válida, caso contrário, false.
 */
function isValidAPPartialTransition(label) {
  if (label.length === 0) {
    return true;
  }

  const transitions = label.split(",").map((t) => t.trim());

  for (const transition of transitions) {
    // Regex para validar cada parte da transição incompleta.
    // Permite estruturas como 'a', 'a X', 'a X/', 'a X/Y' (com ou sem espaços)
    const regexPartial =
      /^\s*([a-zA-Z0-9λ]?\s*([a-zA-Z0-9λ]?\s*(\/([a-zA-Z0-9λ]?)?)?)?)?\s*$/;

    // Testa o padrão completo (símbolo_lido espaço desempilhar/empilhar)
    const fullRegex = /^\s*[a-zA-Z0-9λ]\s+[a-zA-Z0-9λ]+\/[a-zA-Z0-9λ]+\s*$/;

    // Se a transição parcial não se encaixar em nenhum dos padrões (parcial ou completo)
    if (!regexPartial.test(transition) && !fullRegex.test(transition)) {
      return false;
    }
  }

  return true;
}

/**
 * @name isValidMTPartialTransition
 * @description Valida uma transição parcial para MT. Permite a entrada incompleta.
 * Ex: '', 'a', 'a/', 'a/a', 'a/a R', 'a/a R,'
 * @param {string} label O rótulo da transição.
 * @returns {boolean} Retorna true se a string parcial for válida, caso contrário, false.
 */
function isValidMTPartialTransition(label) {
  if (label.length === 0) {
    return true;
  }

  const transitions = label.split(",").map((t) => t.trim());

  for (const transition of transitions) {
    // Regex para validar a transição parcial.
    // Permite estruturas como 'a', 'a/', 'a/b', 'a/b R' (com ou sem espaços)
    const regexPartial =
      /^\s*([a-zA-Z0-9λ#]?\s*(\/([a-zA-Z0-9λ#]?)?\s*([LREDS]?)?)?)?\s*$/;
    // Testa o padrão completo da transição (símbolo_lido/símbolo_escrito direção)
    const fullRegex = /^\s*[a-zA-Z0-9λ#]\s*\/\s*[a-zA-Z0-9λ#]\s*[LREDS]\s*$/;

    // Se a transição parcial não se encaixar em nenhum dos padrões (parcial ou completo)
    if (!regexPartial.test(transition) && !fullRegex.test(transition)) {
      return false;
    }
  }

  return true;
}

/**
 * @name isValidALLPartialTransition
 * @description Valida uma transição parcial para ALL. Permite a entrada incompleta. Valida parcial APENAS para ALL (permitindo <, > e #).
 * @param {string} label O rótulo da transição.
 * @returns {boolean} Retorna true se a string parcial for válida, caso contrário, false.
 */
function isValidALLPartialTransition(label) {
  if (label.length === 0) {
    return true;
  }

  const transitions = label.split(",").map((t) => t.trim());

  for (const transition of transitions) {
    // Regex parcial:
    // Permite: 'a', '<', '>/', 'a/<', 'a/a R', etc.
    const regexPartial =
      /^\s*([a-zA-Z0-9λ<>#]?\s*(\/([a-zA-Z0-9λ<>#]?)?\s*([LREDS]?)?)?)?\s*$/;
    const fullRegex =
      /^\s*[a-zA-Z0-9λ<>#]\s*\/\s*[a-zA-Z0-9λ<>#]\s*[LREDS]\s*$/;

    // Se a transição parcial não se encaixar em nenhum dos padrões
    if (!regexPartial.test(transition) && !fullRegex.test(transition)) {
      return false;
    }
  }

  return true;
}

/**
 * @name validatePartialTransition
 * @description Função principal para validar o rótulo de uma transição de forma parcial, com base no tipo de autômato.
 * @param {string} label O rótulo da transição.
 * @param {string} automatonType O tipo de autômato (ex: 'AF', 'AP', 'ALL', 'MT').
 * @returns {boolean} Retorna true se a string parcial for válida, caso contrário, false.
 */
export function validatePartialTransition(label, automatonType) {
  const trimmedLabel = label.trim();

  const partialValidators = {
    AF: isValidAFPartialTransition,
    AP: isValidAPPartialTransition,
    ALL: isValidALLPartialTransition,
    MT: isValidMTPartialTransition,
  };

  const validator = partialValidators[automatonType];

  if (validator) {
    return validator(trimmedLabel);
  }

  // Se o tipo de autômato não for reconhecido, retorne true para não bloquear a entrada.
  return true;
}
